import { Box } from "@mui/material";
import Button from "components/common/Button";
import ExportButton from "components/common/Export";
import { fetchmembersInfo } from "components/common/Filters/Filters.slice";
import { progressTypesFilterValue } from "components/common/Filters/FiltersHelper";
import GlobalFilterDescription from "components/common/Filters/GlobalFilterDescription";
import GlobalFilters from "components/common/Filters/GlobalFilters";
import FunctionalAreas from "components/common/FunctionalAreas";
import Spinner from "components/common/Spinner/Spinner";
import BasicTable from "components/common/Table";
import Typography from "components/common/Typography";
import { useTranslate } from "hooks/useTranslate";
import { isEmpty, uniq } from "lodash";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { DEFAULT_CORP_INNCODE, progressTypes } from "utils/CommonConstants";
import { formFilterRequestPayload } from "utils/CommonFunctions";
import { getUserNameWithConditionalCheck } from "utils/SharedUtils";
import { serviceTypes } from "utils/Types";
import { getAssignmentData, getPlanProgressData, getUserPlanProgressData, getpropertyProgressData } from "./Data";
import { fetchAssigneeDetailsProgressAsync, fetchProgressTableInfoAsync, setProgressLoader } from "./Progress.slice";
import "./index.scss";

interface Props {
  progressType: string;
}

const ProgressInfo = (props: Props) => {
  const { progressType } = props;

  const translate = useTranslate();
  const dispatch = useAppDispatch();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const summaryDetail = useAppSelector((state) => state.progress.progressInfo.summaryDetail);
  const selectedList = useAppSelector((state: any) => state.CorpDashboard.myListsSelected.selectedList);
  const {
    tableDetails: tableData,
    totalRecords,
    meta
  } = useAppSelector((state) => state.progress?.progressInfo?.[progressType]) || [];
  const progressLoader = useAppSelector((state) => state.progress.progressLoader);
  const allUsers = useAppSelector((state) => state.progress.progressInfo.allUsers);
  const functionalAreaList = useAppSelector((state) => state.functionalAreas.list);
  const globalFiltersforPage = useAppSelector((state) => state.globalFilters[progressTypesFilterValue[progressType]]);
  const filterRequestPayload = globalFiltersforPage?.modifiedFilter || globalFiltersforPage?.defaultFilter || [];
  const formattedFilterRequestPayload = formFilterRequestPayload(filterRequestPayload);

  const [pageNo, setPageNo] = useState(0);
  const searchText = globalFiltersforPage?.searchText || "";

  const searchParams = new URLSearchParams(location.search);
  const cmsId = searchParams.get("cmsId");
  const users = searchParams.get("users");
  const inncode =
    progressType === progressTypes.plan ? summaryDetail?.implementedInncodes?.join(",") : searchParams.get("inncode");

  const sorting = useAppSelector((state) => state.sortingDetails.sorting);
  const recentSortingApplied = sorting?.[`${progressType}ProgressScreen`];

  const progressTypeDetails: any = {
    [progressTypes.plan]: {
      label: "plan",
      buttonLabel: "View Plan Details",
      nameKey: "planName",
      tableDetails: getPlanProgressData(tableData, allUsers, navigate),
      url: `/plandetails/${cmsId}`
    },
    [progressTypes.property]: {
      label: "property",
      buttonLabel: "View Property Details",
      description: { label: "All property plans", detail: "Use the button to see all the plans of the property" },
      nameKey: "propertyName",
      tableDetails: getpropertyProgressData(tableData, allUsers, navigate, functionalAreaList),
      url: `/property/${inncode}`
    },
    [progressTypes.user]: {
      label: "user",
      buttonLabel: "View User Details",
      description: {
        label: "All user plans",
        detail: "You’re consulting the assignments you made to this user. Use the button to see all of his plans"
      },
      nameKey: "innCode",
      tableDetails: getUserPlanProgressData(tableData, allUsers, navigate, functionalAreaList),
      url: `/userview/${users}?inncode=${inncode}`
    }
  };

  const toQueryParams = () => {
    return Object.entries(formattedFilterRequestPayload)
      .map(([key, value]) => `${key}=${Array.isArray(value) ? value.join(",") : value}`)
      .join("&");
  };

  const formUrl = (data: any) => {
    const url = `inncode-groups/export?id=${data.id}&view=plans&includeOwners=${data.includeOwners || false}&cmsId=${
      data.cmsId
    }&inncode=${data.inncode}&status=${data.status}&engagement=${data.engagement}&searchText=${searchText}&property=${
      data.property
    }&order=${data.order}&planName=${encodeURIComponent(data.planName)}&listName=${encodeURIComponent(
      data.listName
    )}&page=0&size=10000`;
    return url;
  };

  const getData = async (pageNumber?: number) => {
    let payload: any = {
      id: params?.listId,
      type: progressType,
      page: pageNumber ?? pageNo,
      property: recentSortingApplied["property"],
      order: recentSortingApplied["order"]
    };
    if (progressType === progressTypes.plan) {
      const { inncode: filterInncode, engagement = "", status = "" } = formattedFilterRequestPayload;
      const url = `inncode=${
        filterInncode?.length ? filterInncode.join(",") : inncode
      }&status=${status}&engagement=${engagement}&searchText=${searchText}`;
      payload = {
        ...payload,
        payloadUrl: `cmsId=${cmsId}&${url}&includeImplementedInnCodeUserMappingsOnly=true`
      };
    } else if (progressType === progressTypes.property) {
      const { functionalAreas, users, status } = formattedFilterRequestPayload;

      const url = `functionalAreas=${functionalAreas?.join(",") || ""}&users=${users || ""}&status=${
        status || ""
      }&searchText=${searchText}&includeImplementedInnCodeUserMappingsOnly=true`;
      payload = {
        ...payload,
        payloadUrl: `inncode=${inncode}&includeImplementedInnCodeUserMappingsOnly=${true}&${url}`
      };
    } else if (progressType === progressTypes.user) {
      const url = toQueryParams();
      payload = {
        ...payload,
        payloadUrl: `inncode=${inncode}&users=${users}&${url}&searchText=${searchText}&includeImplementedInnCodeUserMappingsOnly=true`
      };
    }
    if (pageNumber ?? pageNo === 0) payload = { ...payload, includeOwners: true };
    const tableList = await dispatch(fetchProgressTableInfoAsync(payload));
    const owners = tableList?.payload?.res?.meta?.owners || [];
    const mappedUsers = tableList?.payload?.res?.meta?.mappedUsers || [];
    const assignees = tableList?.payload?.res?.meta?.assignees || [];
    const hltIds = uniq([...assignees, ...owners, ...mappedUsers]);

    if ((pageNumber === 0 || pageNo === 0) && hltIds) {
      const data = {
        hltIds: hltIds && hltIds?.join(","),
        size: hltIds && hltIds?.length
      };
      if (isEmpty(allUsers)) await dispatch(fetchAssigneeDetailsProgressAsync(data));
    }

    dispatch(setProgressLoader(false));
  };

  useEffect(() => {
    getData(0);
    setPageNo(0);
  }, [progressType, recentSortingApplied, filterRequestPayload, searchText]);

  useEffect(() => {
    if (pageNo > 0) getData();
  }, [pageNo]);

  const getMembersList = (searchValue: string) => {
    const props: any = {
      hltIds: meta.assignees,
      page: 0,
      size: 100
    };
    dispatch(fetchmembersInfo(props));
  };

  const exportPlanProgressPayload = () => {
    const { inncode: filterInncode, engagement = "", status = "" } = formattedFilterRequestPayload;
    return formUrl({
      searchText,
      view: "plans",
      includeOwners: false,
      id: params?.listId,
      inncode: filterInncode?.length ? filterInncode.join(",") : inncode,
      status,
      engagement: engagement,
      property: recentSortingApplied["property"],
      order: recentSortingApplied["order"],
      listName: selectedList?.name,
      planName: summaryDetail?.planName,
      cmsId
    });
  };

  return (
    <div className="progress-info-component">
      {progressLoader && <Spinner backdropVisible />}
      <div className="progress-info-summary-container">
        <div
          className={`progress-info-summary-left-wrapper ${
            progressType !== progressTypes.plan ? "property-user-summary" : ""
          }`}
        >
          {progressType !== progressTypes.plan && (
            <Typography variant={"body1"} color={tokens.Text.General.PrimaryHeadline}>
              {progressType === progressTypes.user
                ? getUserNameWithConditionalCheck(
                    summaryDetail?.displayName,
                    summaryDetail?.firstName,
                    summaryDetail?.lastName
                  )
                : summaryDetail?.inncode}
            </Typography>
          )}

          <Typography variant="h4" showBold color={tokens.Text.General.PrimaryHeadline} className="left-wrapper-header">
            {summaryDetail[`${progressTypeDetails[progressType]?.nameKey}`]}
          </Typography>
          {progressType !== "plan" && <> {getAssignmentData(progressType, summaryDetail)}</>}

          {progressType === progressTypes.plan && summaryDetail?.functionalAreas && (
            <div className="left-wrapper-data-info-plan">
              <Box>
                <FunctionalAreas functionalAreaIds={summaryDetail?.functionalAreas} />
              </Box>
            </div>
          )}
        </div>
        {progressType === progressTypes.plan && (
          <div className="right-Wrapper-plan">
            {" "}
            <Button
              label={progressTypeDetails[progressType]?.buttonLabel}
              size="Small"
              onClick={() =>
                navigate(progressTypeDetails[progressType]?.url, {
                  state: {
                    innCode: DEFAULT_CORP_INNCODE
                  }
                })
              }
            />
          </div>
        )}

        {progressType !== progressTypes.plan && (
          <div className="right-Wrapper-property-user">
            {" "}
            <div className="right-Wrapper-property-user-text">
              <Typography variant={"body2"} showBold color={tokens.Text.General.PrimaryHeadline}>
                {progressTypeDetails?.[progressType]?.description?.label}
              </Typography>
              <Typography variant={"body2"} color={tokens.Text.General.PrimaryHeadline}>
                {progressTypeDetails[progressType]?.description?.detail}
              </Typography>
            </div>
            <Button
              label={progressTypeDetails[progressType]?.buttonLabel}
              onClick={() => navigate(progressTypeDetails[progressType]?.url)}
              size="Small"
            />
          </div>
        )}
      </div>
      <div id="progressDiv" className="progress-info-table-container">
        <>
          <div className={`new-filters ${progressType === progressTypes.plan ? "horizontal-end" : ""}`}>
            {totalRecords > 0 && progressType === progressTypes.plan && (
              <ExportButton
                URL={exportPlanProgressPayload()}
                isGet={true}
                data={exportPlanProgressPayload()}
                serviceType={serviceTypes.userplan}
              />
            )}
            <GlobalFilters
              isPreDefinedNav
              page={progressTypesFilterValue[progressType]}
              searchedText={searchText}
              isSearchBarVisible={progressType === progressTypes.user || progressType === progressTypes.property}
              searchBarPlaceholder={progressType === progressTypes.user ? "Search by plan name" : "Search by name"}
              getMembersList={getMembersList}
            />
          </div>
          <GlobalFilterDescription page={progressTypesFilterValue[progressType]} />
        </>
        <BasicTable
          scrollableTarget="progressDiv"
          totalRecords={totalRecords}
          pagination={{ pageNo: pageNo, setPageNo: setPageNo }}
          noDataMessage={translate("common.noResults")}
          data={progressTypeDetails[progressType]?.tableDetails}
          tableName={`${progressType}ProgressScreen`}
        />
      </div>
    </div>
  );
};
export default ProgressInfo;
